import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMessagingServiceWorkerRegistration, getNotificationToken } from "../../../..";
import { UpdateUserParams } from "../../../profile/domain/usecases/updateUser";
import { onUpdateUser } from "../../../profile/presentation/store/OnboardingThunk";
import { AppDispatch, RootState } from "../../../../core/store/store";
import UserInfo from "../../../profile/domain/entities/user";
import { changePushState } from "../../../profile/presentation/store/OnboardingSlice";
import {
	registerBrowserPushSubscription,
	saveBrowserPushSubscription,
	supportsBrowserPushSubscription,
} from "../../../../core/utilities/browserPushSubscription";
import {
	hasSavedPushNotificationToken,
	markPushNotificationTokenRefreshed,
	needsPushNotificationTokenRefresh,
	savePushSubscription,
	savePushNotificationToken,
	shouldResavePushSubscription,
} from "../../../../core/utilities/pushNotificationSync";

export const useActivateNotifications = () => {
	const dispatch: AppDispatch = useDispatch();
	const userId = useSelector((state: RootState) => state.onboarding.user?.id);

	const activateNotifications = useCallback(async ({
		requestPermission = true,
		forceRefresh = false,
	}: {
		requestPermission?: boolean;
		forceRefresh?: boolean;
	} = {}) => {
		const shouldForceRefresh = forceRefresh || needsPushNotificationTokenRefresh();
		const token = await getNotificationToken({
			requestPermission,
			forceRefresh: shouldForceRefresh,
		});
		if (token) {
			dispatch(changePushState('enabled'));
			if (userId && !shouldForceRefresh && hasSavedPushNotificationToken(userId, token)) {
				// Keep going so the installed iPhone PWA can also save its browser subscription.
			} else {
				const updateParams = new UpdateUserParams({ token_push_notification: token } as UserInfo);
				const result = await dispatch(onUpdateUser(updateParams));
				if (userId && onUpdateUser.fulfilled.match(result)) {
					savePushNotificationToken(userId, token);
					markPushNotificationTokenRefreshed();
				}
			}
		}

		if (
			userId &&
			typeof Notification !== "undefined" &&
			Notification.permission === "granted" &&
			supportsBrowserPushSubscription()
		) {
			try {
				const registration = await getMessagingServiceWorkerRegistration();
				if (registration) {
					const subscription = await registerBrowserPushSubscription(registration, {
						forceRefresh: shouldForceRefresh,
					});
					const endpoint = subscription?.endpoint ?? null;
					if (
						subscription &&
						endpoint &&
						(shouldResavePushSubscription(userId, endpoint) || shouldForceRefresh)
					) {
						await saveBrowserPushSubscription(subscription);
						savePushSubscription(userId, endpoint);
					}
				}
			} catch (error) {
				console.warn("Could not save browser push subscription:", error);
			}
		}
		return token ?? null;
	}, [dispatch, userId]);

	return activateNotifications;
};
